<template>
  <el-table
    class="candidate-list"
    row-key="id"
    ref="candidateList"
    tooltip-effect="light"
    height="100%"
    :default-sort="{prop: 'join_time', order: 'descending'}"
    :data="data"
    :empty-text="$t('el.table.emptyText')"
    @sort-change="handleSortChange"
    @selection-change="handleSelectionChange"
    @select-all="handleSelectAll"
  >
    <el-table-column width="35px" prop="id" type="selection" class-name="selection" :selectable="(row) => !row.is_agency_uploaded_and_hid"></el-table-column>
    <el-table-column min-width="120px" prop="candidate" :label="$t(`${i18nPrefix}.label.candidateName`)">
      <template #default="{ row }">
        <div
          class="avatar-name-container"
          :class="[row.candidate.is_employee && 'employee', row.candidate.is_alumni && 'alumni']"
        >
          <el-tooltip
            effect="light"
            popper-class="light-tooltip-popper"
            :content="row.candidate.candidateStatusName"
          >
            <div class="avatar-container">
              <img class="avatar" :src="getCandidateAvatarPath(row)" @click="handleAvatarClick(row)"/>
              <i v-if="row.is_agency_uploaded_and_hid" class="avatar-lock-icon iconfont iconLock"></i>
            </div>
          </el-tooltip>
          <div class="name-container">
            <el-tooltip effect="light" popper-class="light-tooltip-popper" :content="row.candidate.name">
              <div class="name" @click="handleNameClick(row)">{{ row.candidate.name }}</div>
            </el-tooltip>
            <div class="candidate-icon-container">
              <el-tooltip
                v-if="row.referral_type === REFERRAL_TYPE.EMPLOYEE_SHARED || row.referral_type === REFERRAL_TYPE.CANDIDATE_SHARED"
                effect="light"
                popper-class="light-tooltip-popper"
                placement="bottom"
                :content="$t(`${i18nPrefix}.label.${row.referral_type === REFERRAL_TYPE.EMPLOYEE_SHARED ? 'employeeReferral' : 'candidateReferral'}`)"
              >
                <span class="candidate-icon referral">
                  <i class="iconfont iconreferral"></i>
                </span>
              </el-tooltip>
              <DuplicateCandidatePopover
                v-if="hasDuplicateCandidatePermission && row.duplicate_candidate_list && row.duplicate_candidate_list.length > 0"
                class="candidate-icon duplicate-candidate"
                :duplicateCandidateList="row.duplicate_candidate_list"
                @openDuplicateCandidateDetail="openDuplicateCandidateDetail"
              >
                <DuplicateCandidateIcon :size="14"></DuplicateCandidateIcon>
              </DuplicateCandidatePopover>
              <el-tooltip
                v-if="row.referral_type === REFERRAL_TYPE.EMPLOYEE_SUBMITTED || row.referral_type === REFERRAL_TYPE.CANDIDATE_SUBMITTED"
                effect="light"
                popper-class="light-tooltip-popper"
                placement="bottom"
                :content="$t(`${i18nPrefix}.label.${row.referral_type === REFERRAL_TYPE.EMPLOYEE_SUBMITTED ? 'employeeSubmitted' : 'candidateSubmitted'}`)"
              >
                <span  class="candidate-icon submit">
                  <i class="iconfont icona-bianzu53"></i>
                  <i class="iconfont iconxingzhuang dot" :style="dotStyle"></i>
                </span>
              </el-tooltip>
            </div>
          </div>
        </div>
      </template>
    </el-table-column>
    <el-table-column min-width="180px" prop="recent_work_experience" show-overflow-tooltip :label="$t(`${i18nPrefix}.label.workCompanyName`)">
      <template #default="{ row }">{{ (row.recent_work_experience && row.recent_work_experience.company)|empty }}</template>
    </el-table-column>
    <el-table-column min-width="125px" prop="recent_work_experience" show-overflow-tooltip :label="$t(`${i18nPrefix}.label.lastWorkJobName`)">
      <template #default="{ row }">{{ (row.recent_work_experience && row.recent_work_experience.job)|empty }}</template>
    </el-table-column>
    <el-table-column min-width="120px" prop="max_degree" show-overflow-tooltip sortable="custom" :sort-orders="['descending', 'ascending']"  :label="$t(`${i18nPrefix}.label.degree`)">
      <template #default="{ row }">{{ row.candidate.maxDegreeI18nName|empty }}</template>
    </el-table-column>
    <el-table-column min-width="75px" prop="candidate" label-class-name="age-gender-header" :label="$t(`${i18nPrefix}.label.ageAndGender`)">
      <template #default="{ row }">
        <span class="age">{{ row.candidate.age }}</span>
        <span v-if="row.candidate.gender === 'M' || row.candidate.gender === 'F'" class="gender">
          <i
            class="iconfont"
            :class="`iconResume-${row.candidate.gender === 'M' ? 'male' : 'female'}`"
          />
        </span>
      </template>
    </el-table-column>
    <el-table-column min-width="95px" prop="candidate" show-overflow-tooltip :label="$t(`${i18nPrefix}.label.location`)">
      <template #default="{ row }">{{ (row.candidate.locationInfo && row.candidate.locationInfo)|empty }}</template>
    </el-table-column>
    <el-table-column min-width="100px" prop="join_time" sortable="custom" :sort-orders="['descending', 'ascending']" :label="$t(`${i18nPrefix}.label.joinedStartTimeJoinedEndTime`)">
      <template #default="{ row }">{{ row.join_time|formatDate }}</template>
    </el-table-column>
    <el-table-column v-if="hasCRMTagPermission" min-width="160px" prop="tag_list" class-name="tags" :label="$t(`${i18nPrefix}.label.tag`)">
      <template #default="{ row }">
        <CandidateListTagCellVue :tag-list="row.tag_list" />
      </template>
    </el-table-column>
    <el-table-column width="45px" prop="id">
      <template #default="{ row }">
        <el-dropdown v-if="!row.is_agency_uploaded_and_hid" class="operation-btn operation-btn-dropdown" trigger="click" @command="handleOperation($event, row)">
          <i class="btn-icon el-icon-more"></i>
          <template #dropdown>
            <el-dropdown-menu class="no-arrow-dropdown-menu">
                <el-dropdown-item
                  v-for="(item, index) in operationList"
                  :key="index"
                  :command="item"
                >
                  <span v-if="!item.childrenMenuList">{{ item.label }}</span>
                  <el-popover
                    v-if="item.childrenMenuList"
                    placement="left"
                    v-model="item.visible"
                    trigger="hover"
                    popper-class="action-children-container"
                    :ref="`popover_${row.id}`"
                  >
                    <div
                      v-for="(child, childIndex) in item.childrenMenuList"
                      v-show="!child.hidden"
                      class="child-item"
                      :key="childIndex"
                      @click="handleOperation(child, row, item)"
                    >
                      {{ child.label }}
                    </div>
                    <div slot="reference">{{ item.label }}</div>
                  </el-popover>
                </el-dropdown-item>
              </el-dropdown-menu>
          </template>
        </el-dropdown>
      </template>
    </el-table-column>
  </el-table>
</template>

<script>
import moment from 'moment'
import { getI18nPropertyValue } from '@/i18n'
import { REFERRAL_TYPE } from '@/constant/application'
import _ from 'lodash'
import DuplicateCandidatePopover from '@/views/adminNew/application/components/duplicateCandidatePopover.vue'
import DuplicateCandidateIcon from '@/components/icon/duplicateCandidate.vue'
import CandidateListTagCellVue from '@/components/candidate/candidateListTagCell.vue'

export default {
  components: {
    DuplicateCandidateIcon,
    DuplicateCandidatePopover,
    CandidateListTagCellVue
  },
  props: {
    data: {
      type: Array,
      required: true
    },
    operationList: {
      type: Array,
      required: true
    },
    cancelSelectedCandidateList: {
      type: Array,
      required: true
    }
  },
  filters: {
    empty (data, defaultText) {
      if (data === null || data === undefined) {
        return defaultText || '-'
      } else {
        return data
      }
    }
  },
  metaInfo () {
    return {
      style: [
        {
          innerHTML: `.el-tooltip__popper.is-light {
            border-color: #e6e6e6;
            background: #e6e6e6;
            -webkit-box-shadow: 0px 6px 16px 0px rgba(0, 0, 0, 0.08), 0px 3px 6px -4px rgba(0, 0, 0, 0.12);
                    box-shadow: 0px 6px 16px 0px rgba(0, 0, 0, 0.08), 0px 3px 6px -4px rgba(0, 0, 0, 0.12);
          }
          .el-tooltip__popper.is-light[x-placement] .popper__arrow {
            border-bottom-color: #e6e6e6;
            border-top-color: #e6e6e6;
          }
          .el-tooltip__popper.is-light[x-placement] .popper__arrow::after {
            border-bottom-color: #e6e6e6;
            border-top-color: #e6e6e6;
          }`
        }
      ]
    }
  },
  data () {
    return {
      i18nPrefix: 'admin.application.applicantList',
      moment,
      getI18nPropertyValue,
      REFERRAL_TYPE,
      atBottom: false,
      emitScrollBottom: null,
      dotColor: '#51bf82',
      size: 14
    }
  },
  computed: {
    hasDuplicateCandidatePermission () {
      return this.hasAnyPermission('PREMIUM_DUPLICATE_CANDIDATE')
    },
    dotStyle () {
      const dotSize = this.dotSize ? this.dotSize : this.size * 0.6
      const style = { color: this.dotColor }
      if (dotSize >= 12) {
        style.fontSize = `${dotSize}px`
      } else {
        style.fontSize = '12px'
        style.transform = `scale(${dotSize / 12})`
      }
      return style
    },
    hasCRMTagPermission () {
      return this.hasAnyPermission('PREMIUM_TAG_MANAGEMENT')
    }
  },
  created () {
    this.emitScrollBottom = _.debounce(() => {
      this.$emit('scrollBottom')
    }, 200)
  },
  mounted () {
    const el = this.$el.querySelector('.el-table__body-wrapper')
    el.addEventListener('scroll', () => {
      const distance = el.scrollHeight - el.scrollTop - el.clientHeight - 10
      if (distance <= 50) {
        if (!this.atBottom) {
          this.emitScrollBottom()
        }
        this.atBottom = true
      } else {
        this.atBottom = false
      }
    })
  },
  methods: {
    refreshSelectAllCandidate () {
      const noSelectedList = _.concat([], this.cancelSelectedCandidateList, _.filter(this.data, item => item.is_agency_uploaded_and_hid))
      this.$refs.candidateList.store.states.selection = _.difference(this.data, noSelectedList)
    },
    handleSortChange ({ prop, order }) {
      this.$emit('orderChange', prop, order === 'descending' ? '-' : '')
    },
    handleSelectionChange (val) {
      this.$emit('selectedChange', val)
    },
    handleSelectAll (val) {
      this.$emit('selectAll', val)
    },
    handleOperation (operation, row, item) {
      if (item && item.childrenMenuList) {
        const popoverRef = this.$refs[`popover_${row.id}`][1]
        popoverRef.doClose()
      }
      operation.operator && operation.operator(row)
    },
    handleNameClick (row) {
      this.$emit('openDetail', row)
    },
    handleAvatarClick (row) {
      this.$emit('openDetail', row)
    },
    getCandidateAvatarPath (row) {
      return row.is_agency_uploaded_and_hid ? this.$DjangoStaticUrl('images_en/default.jpg') : this.$DjangoBaseUrl(row.candidate.photo)
    },
    openDuplicateCandidateDetail (application) {
      this.$emit('openDuplicateCandidateDetail', application)
    },
    scrollToTop () {
      const el = this.$el.querySelector('.el-table__body-wrapper')
      el.scrollTo({
        top: 0
      })
    }
  },
  watch: {
    data () {
      this.$nextTick(() => {
        const tableRef = this.$refs.candidateList
        if (tableRef) {
          tableRef.layout.updateElsHeight()
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.candidate-list {
  .avatar-name-container {
    display: flex;
    align-items: center;

    &.employee {
      .avatar-container {
        .avatar {
          border-color: #0096cc;
        }
      }
    }

    &.alumni {
      .avatar-container {
        .avatar {
          border-color: #ee7815;
        }
      }
    }

    .avatar-container {
      position: relative;
      flex-shrink: 0;

      .avatar-lock-icon {
        position: absolute;
        bottom: 2px;
        right: 6px;
        font-size: 14px;
        color: #9e9e9e;
      }

      .avatar {
        display: block;
        width: 44px;
        height: 44px;
        padding: 1px;
        border: 1px solid transparent;
        border-radius: 50%;
        line-height: 1;
        cursor: pointer;
      }
    }

    .name-container {
      flex: 1;
      margin-left: 22px;
      overflow: hidden;

      .name {
        font-size: 15px;
        white-space: nowrap;
        word-break: keep-all;
        overflow: hidden;
        text-overflow: ellipsis;
        cursor: pointer;
      }

      .candidate-icon-container {
        line-height: 16px;

        .candidate-icon {
          cursor: pointer;

          & + .candidate-icon {
            margin-left: 5px;
          }

          &.submit, &.referral {
            .iconfont {
              font-size: 14px;
            }
          }

          &.submit {
            position: relative;

            .dot {
              border-radius: 50%;
              background: #fff;
              position: absolute;
              right: -3px;
              bottom: -3px;
              z-index: 1;
            }
          }
        }
      }
    }
  }

  .gender {
    margin-left: 2px;
  }

  .operation-btn {
    cursor: pointer;

    .btn-icon {
      transform: rotateZ(90deg);
    }
  }

  ::v-deep {
    .cell {
      line-height: 24px;
    }

    .el-checkbox__inner {
      width: 18px;
      height: 18px;

      &::after {
        height: 9px;
        left: 6px;
        top: 2px;
      }
    }

    .selection {
      .cell {
        padding: 0;
        text-align: center;
      }
    }

    .age-gender-header {
      word-break: break-word;
    }
  }
}

.action-children-container {
  padding: 10px 0;

  .child-item {
    line-height: 36px;
    padding: 0 20px;
    margin: 0;
    cursor: pointer;

    &:hover {
      color: #66b1ff;
      background-color: #ecf5ff;
    }
  }
}
</style>
